// ── SettingsEstimationPage ────────────────────────────────────────────────────
//
// CFGIA.B1 (#1541) — Settings → Estimation. Surfaces the shared EstimationCard
// on a standalone settings page so admins (and members in read-only) can
// configure or view the tenant's estimation display after onboarding.
//
// D13 (card-gating rule): estimation is member-read / admin-write → the page
// renders for all members; canEdit={isAdmin} gates the write. Matches the
// Deployment and Agents pattern.

import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import EstimationCard from '../components/settings/EstimationCard';
import { HelpPopover } from '../components/help';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: 20,
  marginBottom: 16,
};

export default function SettingsEstimationPage() {
  const { myTenant, loading } = useMyTenant();
  const tenantId = myTenant?.tenantId;
  const isAdmin = isAdminRole(myTenant?.role);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <Sidebar />
      <main style={{ flex: 1, padding: 32, maxWidth: 760 }}>
        <div style={{ marginBottom: 16 }}>
          <Link
            to="/settings"
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'var(--text-secondary)',
              textDecoration: 'none',
            }}
          >
            ← Back to settings
          </Link>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 700,
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Estimation
          </h1>
          <HelpPopover
            id="estimation-settings"
            variant="info"
            heading="Estimation display"
            body={
              <p style={{ margin: 0 }}>
                Choose whether effort estimates appear as Story Points, EU/RU (Execution + Review
                Units), or both side by side. Changes apply tenant-wide and take effect on the next
                session. Only tenant admins can edit.
              </p>
            }
          />
        </div>
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            color: 'var(--text-secondary)',
            marginBottom: 24,
          }}
        >
          Choose how effort estimates appear across your projects.
        </p>

        {loading || !tenantId ? (
          <div style={{ ...cardStyle, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>
            Loading…
          </div>
        ) : (
          <div style={cardStyle}>
            <EstimationCard
              tenantId={tenantId}
              canEdit={isAdmin}
              showIntro={false}
            />
          </div>
        )}
      </main>
    </div>
  );
}
